import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../app/hooks";
import { selectIsOrgOwner } from "../authentication/authenticationSlice";
import { NO_CURRENCY } from "../billing/utils/money";
import { CURRENCY_OPTIONS, currencyOptionLabel } from "./utils/currencyOptions";

interface Props {
  value: string;
  onChange: (currency: string) => void;
}

/**
 * Billing-currency dropdown shown in settings. The currency belongs to the
 * organization: every instructor inherits it, so only the organization owner
 * can change it — everyone else sees it read-only.
 */
const BillingCurrencySelector = ({ value, onChange }: Props) => {
  const { t, i18n } = useTranslation();
  const isOrgOwner = useAppSelector(selectIsOrgOwner);

  return (
    <div className="border-t border-grey-80 dark:border-grey-50 pt-5 space-y-2">
      <p className="text-sm font-semibold text-grey-30 dark:text-grey-90">
        {t("schedulePreferences.billingCurrency")}
      </p>
      <p className="text-sm text-grey-50 dark:text-grey-70">
        {isOrgOwner
          ? t("schedulePreferences.billingCurrencyOwner")
          : t("schedulePreferences.billingCurrencyReadOnly")}
      </p>
      <select
        className="input w-full sm:w-64"
        value={value}
        disabled={!isOrgOwner}
        onChange={(e) => onChange(e.target.value)}
      >
        {/* A legacy value outside the curated list stays selectable so the form round-trips. */}
        {!CURRENCY_OPTIONS.some((option) => option.code === value) && (
          <option value={value}>{value}</option>
        )}
        {CURRENCY_OPTIONS.map((option) => (
          <option key={option.code} value={option.code}>
            {option.code === NO_CURRENCY
              ? t("schedulePreferences.billingCurrencyNone", "No currency — plain numbers")
              : currencyOptionLabel(option.code, i18n.language)}
          </option>
        ))}
      </select>
    </div>
  );
};

export default BillingCurrencySelector;
